/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import { Theme as MaterialTheme, useTheme, Paper, Typography, Container, } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { useContext } from "react";

import { useTranslation } from 'react-i18next';
import 'react-toastify/dist/ReactToastify.css';
import { useErrorBoundary, withErrorBoundary } from 'react-use-error-boundary';
import ErrorMessageText from '../../../common/components/ErrorMessageText';
import DatagridWaitingInvoices from '../components/DatagridWaitingInvoices';
import PartitionChartOutstandingInvoices from '../components/PartitionChartOutstandingInvoices';
import DatagridNewVouchers from '../components/DatagridNewVouchers';
import DatagridOutstandingInvoices from '../components/DatagridOutstandingInvoices';
import NotificationBoard from '../components/NotificationBoard';  // Imported NotificationBoard
import { UserContext } from '../../../layouts/AuthorizedLayout';
import { useAppBarHeight } from '../../../common/CustomHooks';
import { digiAccActorType } from '../../../common/TextTypes';


const paperXlHeight = (theme: MaterialTheme) => css({
    [theme.breakpoints.up("xl")]: {
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        height: "calc(50vh - " + theme.spacing(3) + ")"
    },
    [theme.breakpoints.down("xl")]: {
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        height: "26rem",
    },
})

const HomeRoute = withErrorBoundary((): JSX.Element => {

    const theme = useTheme();

    const { t, i18n } = useTranslation();

    const user = useContext(UserContext);

    const appBarHeight = useAppBarHeight();

    const OutstandingInvoice = (props: { render: boolean, type: digiAccActorType }): JSX.Element => {

        const theme = useTheme();
        const { t, i18n } = useTranslation();

        if (props.render) {
            return <Paper css={paperXlHeight(theme)}>
                <Grid container alignItems="center">
                    <Grid display="flex" alignItems="center">
                        <Typography variant="body1" fontWeight="bold">{t("home.outstandingInvoice") + " (" + t("basic." + props.type.toLowerCase()) + ")"}</Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={2} flex="1 1 auto">
                    <Grid xs={12} xl={7}>
                        <DatagridOutstandingInvoices type={props.type} />
                    </Grid>
                    <Grid xs={12} xl={5}>
                        <PartitionChartOutstandingInvoices chartName={"Outstanding" + props.type} type={props.type} />
                    </Grid>
                </Grid>
            </Paper>
        } else {
            return <></>
        }
    }
    const WaitingInvoices = (props: { render: boolean }): JSX.Element => {

        if (props.render) {
            return <Paper css={paperXlHeight(theme)}>
                <Grid container alignItems="center">
                    <Grid display="flex" alignItems="center">
                        <Typography variant="body1" fontWeight="bold">{t("home.approver") + " - " + t("home.waitingForYou")}</Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={0} flex="1 1 auto">
                    <DatagridWaitingInvoices type="waitingYou" />
                </Grid>
                <Grid container paddingTop={theme.spacing(2)} alignItems="center">
                    <Grid display="flex" alignItems="center">
                        <Typography variant="body1" fontWeight="bold">{t("home.approver") + " - " + t("home.waitingForOthers")}</Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={0} flex="1 1 auto">
                    <DatagridWaitingInvoices type="waitingOthers" />
                </Grid>
            </Paper>
        } else {
            return <></>
        }
    }
    const NewVouchers = (props: { render: boolean }): JSX.Element => {

        if (props.render) {
            return <Paper css={paperXlHeight(theme)}>
                <Grid container paddingLeft={theme.spacing(1)} paddingRight={theme.spacing(1)} alignItems="center">
                    <Grid display="flex" alignItems="center">
                        <Typography variant="body1" fontWeight="bold">{t("home.newVouchersRecievedInTheLast30days")}</Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={0} flex="1 1 auto">
                    <DatagridNewVouchers />
                </Grid>
            </Paper>
        } else {
            return <></>
        }
    }

    const checkReports = user?.user?.modules?.find(n => n === "Reports");
    const checkApprover = user?.user?.modules?.find(n => n === "Approver");
    const checkVoucherSearch = user?.user?.modules?.find(n => n === "VoucherSearch");

    const UserRights = (): JSX.Element => {
        if (checkReports) {
            return <>
                <Grid container spacing={2} justifyContent="center">
                    <Grid xs={12} md={8}>
                        <OutstandingInvoice render={true} type="Customer" />
                    </Grid>
                    <Grid display={checkApprover ? "block" : "none"} xs={12} md={4}>
                        <WaitingInvoices render={checkApprover ? true : false} />
                    </Grid>
                </Grid>
                <Grid container height={theme.spacing(2)} />
                <Grid container spacing={2} justifyContent="center">
                    <Grid xs={12} md={8}>
                        <OutstandingInvoice render={true} type="Supplier" />
                    </Grid>
                    <Grid display={checkVoucherSearch ? "block" : "none"} xs={12} md={4}>
                        <NewVouchers render={checkVoucherSearch ? true : false} />
                    </Grid>
                </Grid>
            </>
        } else {
            return <Container maxWidth="sm" sx={{
                height: {
                    xs: "calc(100vh - " + appBarHeight + "px -" + theme.spacing(2) + "px )",
                    xl: "calc(100vh - " + theme.spacing(2) + "px )"
                },
                display: "flex", flexDirection: "column"
            }}>
                <Grid container display={checkApprover ? "block" : "none"}>
                    <WaitingInvoices render={checkApprover ? true : false} />
                </Grid>
                <Grid container height={theme.spacing(2)} />
                <Grid container display={checkVoucherSearch ? "block" : "none"}>
                    <NewVouchers render={checkVoucherSearch ? true : false} />
                </Grid>
            </Container>
        }
    }


    const [error, resetError] = useErrorBoundary((error, errorInfo) => {
        console.info("componentDidCatch handler called");
        console.error(error);
        console.error(errorInfo);
    });
    if (error) {
        const message = error instanceof Error ? error.message : (error as string);
        return <ErrorMessageText message={message} />
    }

    return (
        <main style={{ padding: theme.spacing(2) }}>
            <NotificationBoard /> 
            <UserRights />
        </main>
    );
});

export default HomeRoute;
